/**
 * Email template definitions for OA Reporting Email task
 * Each template includes the full email structure with intro, sections, and signature
 */

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  structure: {
    greeting: string;
    intro: string;
    sections: string[];
    closing: string;
    signature: string;
  };
}

export const OA_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'standard',
    name: 'Standard Professional',
    description: 'Balanced professional tone with comprehensive details',
    structure: {
      greeting: 'Dear [Client Name],',
      intro: 'We received a [Action Type] dated [Mailing Date] for your patent application [Application Number]. The Examiner has [summary of rejections/allowances]. You have until [Response Deadline] to respond to this Office Action.',
      sections: [
        'KEY INFORMATION',
        'CLAIMS STATUS',
        'REJECTION DETAILS',
        'PRIOR ART CITED',
        'FORMAL OBJECTIONS',
        'RECOMMENDED NEXT STEPS',
        'IMPORTANT DEADLINES'
      ],
      closing: 'Please let us know if you would like to schedule a call to discuss this Office Action and our recommended response strategy. We recommend addressing this matter well before the deadline to ensure adequate time for a thorough and effective response.',
      signature: 'Best regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]'
    }
  },
  {
    id: 'concise',
    name: 'Executive Summary',
    description: 'Brief, high-level summary focused on key points and action items',
    structure: {
      greeting: 'Dear [Client Name],',
      intro: 'This is to inform you that we received a [Action Type] on [Mailing Date] for Application [Application Number]. Response deadline: [Response Deadline].',
      sections: [
        'SUMMARY',
        'KEY DEADLINES',
        'IMMEDIATE ACTION REQUIRED',
        'NEXT STEPS'
      ],
      closing: 'Please contact us at your earliest convenience to discuss our response strategy.',
      signature: 'Regards,\n\n[Attorney/Agent Name]\n[Firm Name]'
    }
  },
  {
    id: 'detailed',
    name: 'Comprehensive Analysis',
    description: 'In-depth analysis with detailed prior art discussion and strategic recommendations',
    structure: {
      greeting: 'Dear [Client Name],',
      intro: 'I am writing to inform you that our office received a [Action Type] dated [Mailing Date] regarding your patent application [Application Number] entitled "[Application Title]". This correspondence requires our immediate attention and a comprehensive response by [Response Deadline]. Below, I provide a detailed analysis of the Examiner\'s positions and our recommended strategy for response.',
      sections: [
        'EXECUTIVE SUMMARY',
        'OFFICE ACTION DETAILS',
        'CLAIMS STATUS ANALYSIS',
        'DETAILED REJECTION ANALYSIS',
        'PRIOR ART ASSESSMENT',
        'TECHNICAL COMPARISON',
        'FORMAL OBJECTIONS',
        'STRATEGIC RECOMMENDATIONS',
        'PROPOSED AMENDMENTS',
        'ARGUMENT STRATEGY',
        'TIMELINE AND DEADLINES',
        'COST ESTIMATE'
      ],
      closing: 'We recommend scheduling a detailed consultation call within the next week to discuss our proposed response strategy, review potential claim amendments, and address any questions you may have. Our goal is to file a comprehensive and persuasive response that maximizes your chances of securing patent protection for your invention.',
      signature: 'Respectfully,\n\n[Attorney/Agent Name]\n[Registration Number]\n[Firm Name]\n[Phone]\n[Email]'
    }
  },
  {
    id: 'client_friendly',
    name: 'Client-Friendly',
    description: 'Less technical language, more accessible for non-patent professionals',
    structure: {
      greeting: 'Hi [Client Name],',
      intro: 'I wanted to reach out to let you know that we\'ve received an update from the USPTO regarding your patent application [Application Number]. The patent examiner has reviewed your application and provided feedback that we need to address.',
      sections: [
        'WHAT HAPPENED',
        'WHAT THIS MEANS',
        'THE MAIN ISSUES',
        'WHAT WE NEED TO DO',
        'IMPORTANT DATES',
        'NEXT STEPS'
      ],
      closing: 'I know this can seem complex, but this is a normal part of the patent process. We\'re here to guide you through it. Let\'s set up a call this week to discuss the details and our game plan for moving forward.',
      signature: 'Best,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Phone]'
    }
  },
  {
    id: 'allowance',
    name: 'Notice of Allowance',
    description: 'Optimized for positive news - Notice of Allowance',
    structure: {
      greeting: 'Dear [Client Name],',
      intro: 'I am pleased to inform you that we received a Notice of Allowance dated [Mailing Date] for your patent application [Application Number]. This means that the USPTO has approved your application and all claims have been allowed! Your invention will be issued as a United States patent.',
      sections: [
        'CONGRATULATIONS',
        'ALLOWANCE DETAILS',
        'WHAT HAPPENS NEXT',
        'REQUIRED ACTIONS',
        'ISSUE FEE INFORMATION',
        'TIMELINE TO ISSUANCE',
        'FUTURE CONSIDERATIONS'
      ],
      closing: 'This is excellent news and represents a significant milestone for your intellectual property portfolio. Congratulations on this achievement! Please let us know if you would like to discuss continuation strategies or next steps for commercialization.',
      signature: 'Warmest regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]'
    }
  }
];

export const DEFAULT_TEMPLATE_ID = 'standard';

/**
 * Get a template by ID
 */
export function getTemplateById(templateId: string): EmailTemplate | undefined {
  return OA_EMAIL_TEMPLATES.find(t => t.id === templateId);
}

/**
 * Get the default template
 */
export function getDefaultTemplate(): EmailTemplate {
  return OA_EMAIL_TEMPLATES.find(t => t.id === DEFAULT_TEMPLATE_ID) || OA_EMAIL_TEMPLATES[0];
}
